<template>
    <div v-if="jackpot">
        <!-- type -->
        <div class="field is-horizontal">
            <div class="field-label is-normal">
                <label class="label">Type</label>
            </div>
            <div class="field-body">
                <div class="field">
                    <div class="control">
                        <span class="oc-form-text">
                            <template v-if="jackpot.series.seriesLength == 1">Single jackpot</template>
                            <template
                                v-else
                            >Jackpot #{{jackpot.series.inSeriesNumber}} from series of {{jackpot.series.seriesLength || '∞'}} jackpots</template>
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <!-- is shared -->
        <div class="field is-horizontal">
            <div class="field-label is-normal">
                <label class="label">Is shared</label>
            </div>
            <div class="field-body">
                <div class="field">
                    <div class="control">
                        <input type="checkbox" v-model="jackpot.sharedSettings.enabled" disabled>
                    </div>
                </div>
            </div>
        </div>

        <!-- shared bet ttl -->
        <div class="field is-horizontal">
            <div class="field-label is-normal">
                <label class="label">Shared bet ttl</label>
            </div>
            <div class="field-body">
                <div class="field">
                    <div class="control">
                        <input
                            v-model="jackpot.sharedSettings.betTtl"
                            class="input oc-input"
                            type="text"
                            :disabled="true"
                        />
                    </div>
                </div>
            </div>
        </div>

        <!-- id -->
        <div class="field is-horizontal">
            <div class="field-label is-normal">
                <label class="label">ID</label>
            </div>
            <div class="field-body">
                <div class="field">
                    <div class="control">
                        <span class="oc-form-text">{{ jackpot.number }}</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- reseted -->
        <div class="field is-horizontal" v-if="jackpot.state.resetAt">
            <div class="field-label is-normal">
                <label class="label">Reset at</label>
            </div>
            <div class="field-body">
                <div class="field">
                    <div class="control">
                        <span class="oc-form-text">
                            <span v-for="(dt, index) in jackpot.state.resetAt" :key="index">
                                {{ dt | datetime }}
                                <span v-if="index < jackpot.state.resetAt.length - 1">,</span>
                            </span>
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <!-- name -->
        <div class="field is-horizontal">
            <div class="field-label is-normal">
                <label class="label">Name</label>
            </div>
            <div class="field-body">
                <div class="field">
                    <div class="control">
                        <input
                            v-model="jackpot.name"
                            :class="[{ 'is-danger': !$v.jackpot.name }, 'input', 'oc-input']"
                            type="text"
                        />
                    </div>
                </div>
            </div>
        </div>

        <!-- skin -->
        <div class="field is-horizontal">
            <div class="field-label is-normal">
                <label class="label">Skin</label>
            </div>
            <div class="field-body">
                <div class="field">
                    <div class="control">
                        <div class="select">
                            <select v-model="jackpot.skin">
                                <option>bronze</option>
                                <option>silver</option>
                                <option>gold</option>
                                <option>platinum</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- currency -->
        <div class="field is-horizontal">
            <div class="field-label is-normal">
                <label class="label">Currency</label>
            </div>
            <div class="field-body">
                <div class="field">
                    <div class="control">
                        <span class="oc-form-text">{{ jackpot.currency }}</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- series length -->
        <div class="field is-horizontal">
            <div class="field-label is-normal">
                <label class="label">Series length</label>
            </div>
            <div class="field-body">
                <div class="field" :class="{'has-addons': !seriesLength}">
                    <div class="control">
                        <input v-model="seriesLength" class="input oc-input" type="number" />
                    </div>
                    <p v-if="!seriesLength" class="control">
                        <a class="button is-static has-text-danger">infinite</a>
                    </p>
                </div>
            </div>
        </div>

        <!-- state -->
        <div class="field is-horizontal">
            <div class="field-label is-normal">
                <label class="label">Status</label>
            </div>
            <div class="field-body">
                <div class="field">
                    <div class="control">
                        <span class="oc-form-text">
                            <span
                                :class="{'has-text-success has-text-weight-bold': jackpot.state.status == 'active', 'has-text-danger has-text-weight-bold': jackpot.state.status == 'unknown' || jackpot.state.status == 'inactive', 'has-text-grey has-text-weight-bold': jackpot.state.status == 'forbidden'}"
                            >{{ jackpot.state.status }}</span>
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <!-- progress -->
        <div class="field is-horizontal">
            <div class="field-label is-normal">
                <label class="label">Progress</label>
            </div>
            <div class="field-body">
                <div class="field">
                    <div class="control">
                        <span class="oc-form-text">
                            <span
                                :style="{ width: `${calcProgress(jackpot)*2}px` }"
                                class="jackpot-progress"
                            ></span>
                            <span
                                :style="{ width: `${(100 - calcProgress(jackpot))*2}px` }"
                                class="jackpot-progress-alt"
                            ></span>
                            {{ calcProgress(jackpot) }}%
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <!-- start amount -->
        <div class="field is-horizontal">
            <div class="field-label is-normal">
                <label class="label">Start Amount</label>
            </div>
            <div class="field-body">
                <div class="field">
                    <div class="control">
                        <span
                            class="oc-form-text"
                        >{{ jackpot.state.amountStart | currency(currency) }}</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- current amount -->
        <div class="field is-horizontal">
            <div class="field-label is-normal">
                <label class="label">Current Amount</label>
            </div>
            <div class="field-body">
                <div class="field">
                    <div class="control">
                        <currency-input
                            :value="jackpot.state.amountCurrent"
                            :currency="currency"
                            :set="(val) => jackpot.state.amountCurrent = val"
                            class="input oc-input"
                        />
                    </div>
                </div>
            </div>
        </div>

        <!-- next amount -->
        <div class="field is-horizontal">
            <div class="field-label is-normal">
                <label class="label">Next Amount</label>
            </div>
            <div class="field-body">
                <div class="field">
                    <div class="control">
                        <span
                            class="oc-form-text"
                        >{{ jackpot.state.amountNext | currency(currency) }}</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- started at -->
        <div class="field is-horizontal">
            <div class="field-label is-normal">
                <label class="label">Started At</label>
            </div>
            <div class="field-body">
                <div class="field">
                    <div class="control">
                        <span class="oc-form-text">{{ jackpot.state.startedAtUtc | datetime }}</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- if completed -->
        <template v-if="jackpot.state.status == 'completed'">
            <div class="field is-horizontal">
                <div class="field-label is-normal">
                    <label class="label">Ended At</label>
                </div>
                <div class="field-body">
                    <div class="field">
                        <div class="control">
                            <span class="oc-form-text">{{ jackpot.state.endedAtUtc | datetime }}</span>
                        </div>
                    </div>
                </div>
            </div>
            <div class="field is-horizontal">
                <div class="field-label is-normal">
                    <label class="label">Player</label>
                </div>
                <div class="field-body">
                    <div class="field">
                        <div class="control">
                            <span class="oc-form-text">
                                <router-link
                                    :to="{ name: 'player', params: { clientId: clientId, playerId: jackpot.state.winningUserId } }"
                                    target="_blank"
                                >{{ jackpot.state.winningUserId }}</router-link>
                            </span>
                        </div>
                    </div>
                </div>
            </div>
            <div class="field is-horizontal">
                <div class="field-label is-normal">
                    <label class="label">Bet Transaction</label>
                </div>
                <div class="field-body">
                    <div class="field">
                        <div class="control">
                            <span class="oc-form-text">{{ jackpot.state.betTransactionId }}</span>
                        </div>
                    </div>
                </div>
            </div>
            <div class="field is-horizontal">
                <div class="field-label is-normal">
                    <label class="label">Payment Transaction</label>
                </div>
                <div class="field-body">
                    <div class="field">
                        <div class="control">
                            <span class="oc-form-text">{{ jackpot.state.paymentTransactionId }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <!-- limit -->
        <p class="title is-5">Limit Settings</p>
        <div class="field is-horizontal">
            <div class="field-label is-normal">
                <label class="label">Min Amount</label>
            </div>
            <div class="field-body">
                <div class="field has-addons">
                    <p class="control">
                        <currency-input
                            :value="jackpot.limitSettings.amountMin"
                            :currency="currency"
                            :set="(val) => jackpot.limitSettings.amountMin = val"
                            :class="[{ 'is-danger': !$v.jackpot.limitSettings || $v.jackpot.limitSettings.amountMin.$invalid }, 'input', 'oc-input']"
                        />
                    </p>
                    <p class="control">
                        <a class="button is-static">{{ currency }}</a>
                    </p>
                </div>
            </div>
        </div>
        <div class="field is-horizontal">
            <div class="field-label is-normal">
                <label class="label">Max Amount</label>
            </div>
            <div class="field-body">
                <div class="field has-addons">
                    <p class="control">
                        <currency-input
                            :value="jackpot.limitSettings.amountMax"
                            :currency="currency"
                            :set="(val) => jackpot.limitSettings.amountMax = val"
                            :class="[{ 'is-danger': !$v.jackpot.limitSettings || $v.jackpot.limitSettings.amountMax.$invalid }, 'input', 'oc-input']"
                        />
                    </p>
                    <p class="control">
                        <a class="button is-static">{{ currency }}</a>
                    </p>
                </div>
            </div>
        </div>

        <!-- charge -->
        <p class="title is-5">Charge Settings</p>
        <div class="field is-horizontal">
            <div class="field-label is-normal">
                <label class="label">Stake Percent Current</label>
            </div>
            <div class="field-body">
                <div class="field has-addons">
                    <p class="control">
                        <input
                            v-model="jackpot.chargeSettings.stakePercentCurrent"
                            :class="[{ 'is-danger': !$v.jackpot.chargeSettings || $v.jackpot.chargeSettings.stakePercentCurrent.$invalid }, 'input', 'oc-input']"
                            type="number"
                        />
                    </p>
                    <p class="control">
                        <a class="button is-static">%</a>
                    </p>
                </div>
            </div>
        </div>
        <div class="field is-horizontal">
            <div class="field-label is-normal">
                <label class="label">Stake Percent Next</label>
            </div>
            <div class="field-body">
                <div class="field has-addons">
                    <p class="control">
                        <input
                            v-model="jackpot.chargeSettings.stakePercentNext"
                            :class="[{ 'is-danger': !$v.jackpot.chargeSettings || $v.jackpot.chargeSettings.stakePercentNext.$invalid }, 'input', 'oc-input']"
                            type="number"
                        />
                    </p>
                    <p class="control">
                        <a class="button is-static">%</a>
                    </p>
                </div>
            </div>
        </div>

        <!-- trigger -->
        <p class="title is-5">Trigger Settings</p>
        <div class="field is-horizontal">
            <div class="field-label is-normal">
                <label class="label">Amount</label>
            </div>
            <div class="field-body">
                <div class="field">
                    <div class="control">
                        <span
                            class="oc-form-text has-text-weight-bold"
                        >{{ jackpot.triggerSettings.amount | currency(jackpot.currency) }}</span>
                    </div>
                </div>
            </div>
        </div>
        <div class="field is-horizontal">
            <div class="field-label is-normal">
                <label class="label">Min Stake</label>
            </div>
            <div class="field-body">
                <div class="field has-addons">
                    <p class="control">
                        <currency-input
                            :value="jackpot.triggerSettings.stakeMin"
                            :currency="currency"
                            :set="(val) => jackpot.triggerSettings.stakeMin = val"
                            :class="[{ 'is-danger': !$v.jackpot.triggerSettings || $v.jackpot.triggerSettings.stakeMin.$invalid }, 'input', 'oc-input']"
                        />
                    </p>
                    <p class="control">
                        <a class="button is-static">{{ currency }}</a>
                    </p>
                </div>
            </div>
        </div>

        <div class="field is-horizontal">
            <div class="field-label"></div>
            <div class="field-body">
                <div class="field is-grouped">
                    <div class="control">
                        <button
                            :disabled="$v.$invalid"
                            class="button is-primary"
                            @click="save();"
                        >Save</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import state from '../../../../../../state';

import { required, minValue } from 'vuelidate/lib/validators';

import currencyService from '../../../../../../services/currency.service';
import jackpotService from '../../../../../../services/jackpot.service';
import notyService from '../../../../../../services/noty.service';
import breadcrumbsService from '../../../../../../services/breadcrumbs.service';

import CurrencyInput from '../../../../../common/currency-input.vue';

export default {
    name: 'branch-jackpot-edit',
    components: { CurrencyInput },
    data: () => ({
        jackpot: null,
        monitorHost: null
    }),
    computed: {
        branch: function() {
            return this.$route.params.branch;
        },
        jackpotNumber: function() {
            return Number(this.$route.params.jackpotNumber);
        },
        clientId: function() {
            return Number(this.$route.params.clientId) || null;
        },
        currencies: function() {
            return state.currencies.get();
        },
        currency: function() {
            return this.jackpot && this.jackpot.currency;
        },
        seriesLength: {
            get() {
                if (!this.jackpot.series.seriesLength || this.jackpot.series.seriesLength <= 0) this.jackpot.series.seriesLength = null;
                return this.jackpot.series.seriesLength;
            },
            set(value) {
                if (!value || value <= 0) this.jackpot.series.seriesLength = null;
                this.jackpot.series.seriesLength = value;
            }
        }
    },
    validations: {
        jackpot: {
            name: { required },
            currency: { required },
            limitSettings: {
                amountMin: {
                    required,
                    minValue: minValue(0.01)
                },
                amountMax: {
                    required,
                    minValue: minValue(0.01)
                }
            },
            chargeSettings: {
                stakePercentCurrent: {
                    required,
                    minValue: minValue(0.0001)
                },
                stakePercentNext: {
                    required,
                    minValue: minValue(0)
                }
            },
            triggerSettings: {
                stakeMin: {
                    required,
                    minValue: minValue(0)
                }
            }
        }
    },
    async created() {
        await this.loadJackpot();
        breadcrumbsService.dynamic.set({
            key: 'jackpot',
            func: () => {
                let s = dt.local(this.jackpot.state.startedAtUtc).format('D MMM YYYY HH:mm:ss');
                let e = 'in progress';
                if (this.jackpot.state.endedAtUtc) e = dt.local(this.jackpot.state.endedAtUtc).format('D MMM YYYY HH:mm:ss');
                return `${s} - ${e}`;
            }
        });
    },
    methods: {
        async loadJackpot() {
            this.jackpot = await jackpotService.getJackpot(this.jackpotNumber);
        },
        async save() {
            this.branch && (this.jackpot.branch = this.branch);
            this.clientId && (this.jackpot.clientId = this.clientId);
            this.jackpot = await jackpotService.updateJackpot(this.jackpot);
            notyService.success(`Jackpot '${this.jackpot.number}' saved successfully`);
        },
        calcProgress(jackpot) {
            return jackpotService.calcProgress(jackpot);
        }
    }
};
</script>

<style>
.jackpot-progress {
    display: inline-block;
    max-width: 200px;
    text-align: center;
    height: 1em;
    margin: 0;
    background-color: green;
}
.jackpot-progress-alt {
    display: inline-block;
    max-width: 200px;
    text-align: center;
    height: 1em;
    margin: 0;
    border: 1px solid green;
}
</style>



// WEBPACK FOOTER //
// front/src/components/clients/client/branches/branch/jackpots/edit.vue